/* Subtle Aroma — storefront behaviour.
   Custom elements for the header, drawers, cart, search, quantity inputs and
   product forms. No framework, no build step. Motion lives in motion.js. */

(() => {
  const theme = window.theme || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const lockScroll = (on) => {
    document.documentElement.classList.toggle('is-locked', on);
    if (window.lenis) on ? window.lenis.stop() : window.lenis.start();
  };

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const fetchJSON = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
    return data;
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ------------------------------------------------------------------ */
  /* Sticky header                                                       */
  /* ------------------------------------------------------------------ */
  // The header is sticky in CSS, so scrolling never has to move it. This only
  // swaps two classes (solid once scrolled, tucked away while reading down),
  // at most once a frame, and only when they change.
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.queued = false;
      this.onScroll = () => {
        if (this.queued) return;
        this.queued = true;
        requestAnimationFrame(() => {
          this.queued = false;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
    update() {
      const y = window.scrollY;
      const stuck = y > 24;
      if (stuck !== this.stuck) {
        this.stuck = stuck;
        this.classList.toggle('is-stuck', stuck);
      }
      const goingDown = y > this.lastY && y > 480;
      const hide = goingDown && !this.querySelector('details[open]') && !document.documentElement.classList.contains('is-locked');
      if (hide !== this.hidden_) {
        this.hidden_ = hide;
        this.classList.toggle('is-hidden', hide);
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  // Close the desktop mega menu on outside click / Escape; one open at a time.
  document.addEventListener('click', (e) => {
    $$('details[data-mega][open]').forEach((d) => {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  $$('details[data-mega]').forEach((d) => {
    d.addEventListener('toggle', () => {
      if (d.open) $$('details[data-mega][open]').forEach((o) => o !== d && o.removeAttribute('open'));
    });
    // Hover intent on desktop pointers. Hover already opened the panel, so a click on the
    // summary must not toggle it shut again; it keeps the panel open instead.
    if (window.matchMedia('(hover: hover)').matches) {
      let t;
      d.addEventListener('mouseenter', () => { clearTimeout(t); d.setAttribute('open', ''); });
      d.addEventListener('mouseleave', () => { t = setTimeout(() => d.removeAttribute('open'), 160); });
      const summary = d.querySelector('summary');
      if (summary) summary.addEventListener('click', (e) => { if (d.open) e.preventDefault(); });
    }
  });

  /* ------------------------------------------------------------------ */
  /* Drawers                                                             */
  /* ------------------------------------------------------------------ */
  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      });
    }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      requestAnimationFrame(() => {
        const first = $$(FOCUSABLE, $('.drawer__panel', this) || this).find((el) => el.offsetParent !== null);
        (first || this).focus({ preventScroll: true });
      });
    }
    close() {
      clearTimeout(this.openTimer);
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.opener && this.opener.setAttribute) {
        this.opener.setAttribute('aria-expanded', 'false');
        this.opener.focus({ preventScroll: true });
      }
      lockScroll(false);
    }
    trap(e) {
      const items = $$(FOCUSABLE, this).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  // Escape closes whichever panel is open, even if focus has moved to <body>
  // (for example after a cart line the focus was on is removed).
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('.drawer.is-open, .search-modal.is-open').forEach((d) => d.close && d.close());
  });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      document.getElementById(opener.dataset.drawerOpen)?.open(opener);
    }
    const cartOpener = e.target.closest('[data-cart-open]');
    if (cartOpener && theme.cartType === 'drawer' && document.getElementById('CartDrawer')) {
      e.preventDefault();
      document.getElementById('CartDrawer').open(cartOpener);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Quantity input                                                      */
  /* ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        const prev = Number(this.input.value) || 0;
        const min = Number(this.input.min) || 0;
        this.input.value = btn.name === 'plus' ? prev + 1 : Math.max(min, prev - 1);
        if (Number(this.input.value) !== prev) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */
  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    });
  };

  class CartDrawer extends DrawerPanel {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('change', (e) => {
        if (e.target.matches('.qty__input[data-line]')) this.changeLine(e.target.dataset.line, e.target.value);
      });
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-remove-line]');
        if (rm) this.changeLine(rm.dataset.removeLine, 0);
      });
      this.addEventListener(
        'input',
        debounce((e) => {
          if (e.target.matches('[data-cart-note]')) fetchJSON(`${theme.routes.cartUpdate}.js`, { note: e.target.value }).catch(() => {});
        }, 500)
      );
      this.adjustShipping();
    }
    open(opener) {
      super.open(opener);
      this.loadUpsell();
    }
    get contents() {
      return $('[data-cart-contents]', this);
    }
    async changeLine(line, quantity) {
      this.contents?.classList.add('is-loading');
      try {
        const data = await fetchJSON(`${theme.routes.cartChange}.js`, { line: Number(line), quantity: Number(quantity), sections: ['cart-drawer'] });
        this.render(data.sections);
        updateCount(data.item_count);
      } catch (err) {
        console.error(err);
      } finally {
        this.contents?.classList.remove('is-loading');
      }
    }
    render(sections) {
      if (!sections || !sections['cart-drawer']) return;
      const next = $('[data-cart-contents]', parseHTML(sections['cart-drawer']));
      if (next && this.contents) this.contents.innerHTML = next.innerHTML;
      this.adjustShipping();
      this.loadUpsell();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
    // Presentment currencies: the threshold is set in the store currency.
    adjustShipping() {
      const bar = $('[data-ship-bar]', this);
      const rate = Number(window.Shopify?.currency?.rate || 1);
      if (!bar || rate === 1) return;
      const threshold = Number(bar.dataset.threshold) * rate;
      const total = Number(bar.dataset.total);
      const fill = $('[data-ship-fill]', bar);
      if (fill) fill.style.setProperty('--progress', `${Math.min(100, (total / threshold) * 100)}%`);
    }
    async loadUpsell() {
      const box = $('[data-cart-upsell]', this);
      if (!box || box.dataset.loaded === box.dataset.productId || !box.dataset.productId) return;
      box.dataset.loaded = box.dataset.productId;
      try {
        const url = `${theme.routes.recommendations}?section_id=cart-upsell&product_id=${box.dataset.productId}&limit=10&intent=complementary`;
        const html = await (await fetch(url)).text();
        const list = $('.cart-upsell__list', parseHTML(html));
        if (list && list.children.length) {
          box.innerHTML = '';
          box.append(...parseHTML(html).querySelector('[data-upsell-inner]').childNodes);
        }
      } catch {
        /* keep the fallback collection */
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  async function addToCart(form, button) {
    const formData = new FormData(form);
    const id = formData.get('id');
    const drawer = document.getElementById('CartDrawer');
    if (theme.cartType !== 'drawer' || !drawer) return form.submit();

    const label = button && $('[data-label]', button);
    const original = label ? label.textContent : null;
    button?.setAttribute('aria-busy', 'true');
    if (label) label.textContent = theme.strings.adding;
    try {
      // Send the whole form, so the name and options typed into the product
      // options app (properties[...]) reach the cart with the item.
      formData.append('sections', 'cart-drawer');
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      drawer.render(data.sections);
      const cart = await (await fetch(`${theme.routes.cart}.js`)).json();
      updateCount(cart.item_count);
      button?.classList.add('is-added');
      if (label) label.textContent = theme.strings.added;
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { button, id } }));
      // Brief "Added" confirmation, then reveal the drawer, unless the shopper
      // is already in it (adding an upsell) or has closed it in the meantime.
      const alreadyOpen = drawer.classList.contains('is-open');
      clearTimeout(drawer.openTimer);
      drawer.openTimer = setTimeout(() => {
        if (!alreadyOpen) drawer.open(button);
        button?.classList.remove('is-added');
        if (label) label.textContent = original;
      }, alreadyOpen ? 900 : 420);
    } catch (err) {
      if (label) label.textContent = original;
      const errBox = form.querySelector('[data-form-error]');
      if (errBox) {
        errBox.textContent = err.message;
        errBox.hidden = false;
      }
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches('[data-quick-add], [data-product-form]')) return;
    e.preventDefault();
    addToCart(form, e.submitter || $('button[type="submit"]', form));
  });

  /* ------------------------------------------------------------------ */
  /* Search                                                              */
  /* ------------------------------------------------------------------ */
  class SearchModal extends DrawerPanel {
    connectedCallback() {
      super.connectedCallback();
      this.input = $('input[type="search"]', this);
      this.results = $('#PredictiveResults', this);
      this.initial = this.results ? this.results.innerHTML : '';
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-close]')) this.close();
      });
      if (this.input && this.input.hasAttribute('data-predictive')) {
        this.input.addEventListener('input', debounce(() => this.suggest(), 220));
      }
    }
    open(opener) {
      super.open(opener);
      setTimeout(() => this.input?.focus(), 80);
    }
    async suggest() {
      const q = this.input.value.trim();
      this.controller?.abort();
      if (!q) {
        this.results.innerHTML = this.initial;
        return;
      }
      this.controller = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,article&resources[limit]=10&resources[limit_scope]=each`;
        const html = await (await fetch(url, { signal: this.controller.signal })).text();
        const inner = $('[data-predictive-results]', parseHTML(html));
        this.results.innerHTML = inner ? inner.outerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    }
  }
  customElements.define('search-modal', SearchModal);
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-search-open]');
    if (btn) document.getElementById('SearchModal')?.open(btn);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === '/' && !e.target.closest('input, textarea, select, [contenteditable]')) {
      e.preventDefault();
      document.getElementById('SearchModal')?.open();
    }
  });

  /* ------------------------------------------------------------------ */
  /* Localization                                                        */
  /* ------------------------------------------------------------------ */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-localization-select]')) e.target.form.submit();
  });

  /* ------------------------------------------------------------------ */
  /* Product page: variants, gallery, sticky bar                         */
  /* ------------------------------------------------------------------ */
  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (theme.moneyFormat || '${{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
  };

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = $('script[type="application/json"]', this);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.section = this.closest('[data-product-section]');
      this.addEventListener('change', () => this.update());
    }
    update() {
      const selected = $$('input:checked', this).map((i) => i.value);
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      if (!variant) return;
      $$('[data-variant-label]', this).forEach((el, i) => (el.textContent = selected[i]));
      $$('input[name="id"]', this.section).forEach((i) => (i.value = variant.id));
      $$('[data-price-current]', this.section).forEach((el) => (el.textContent = formatMoney(variant.price)));
      $$('[data-price-compare]', this.section).forEach((el) => (el.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price) : ''));
      $$('[data-add-button]', this.section).forEach((btn) => {
        btn.disabled = !variant.available;
        const label = $('[data-label]', btn);
        if (label) label.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      });
      $$('[data-sticky-variant]').forEach((el) => (el.textContent = variant.title));
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }
  customElements.define('variant-picker', VariantPicker);

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.dots = $$('[data-gallery-dot]', this);
      this.thumbs = $$('[data-gallery-thumb]', this);
      this.count = $('[data-gallery-count]', this);
      this.strip = this.thumbs.length ? this.thumbs[0].parentElement : null;
      if (!this.track) return;
      this.track.addEventListener('scroll', debounce(() => this.sync(), 60), { passive: true });
      [...this.dots, ...this.thumbs].forEach((d) =>
        d.addEventListener('click', () => {
          const slide = this.track.children[Number(d.dataset.index)];
          this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        })
      );
    }
    sync() {
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      [...this.dots, ...this.thumbs].forEach((d) => d.classList.toggle('is-active', Number(d.dataset.index) === i));
      if (this.count) this.count.textContent = `${i + 1} / ${this.thumbs.length || this.track.children.length}`;
      // Keep the active thumbnail in view when the strip scrolls sideways (long galleries).
      const thumb = this.thumbs[i];
      if (thumb && this.strip && this.strip.scrollWidth > this.strip.clientWidth) {
        const offset = thumb.getBoundingClientRect().left - this.strip.getBoundingClientRect().left;
        this.strip.scrollTo({ left: this.strip.scrollLeft + offset - (this.strip.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
      }
    }
  }
  customElements.define('product-gallery', ProductGallery);

  // Sticky add-to-cart bar (mobile): visible while the main add-to-cart row is off screen.
  // Globo swaps the main button for a copy that runs its checks, so the bar watches the row
  // (which stays put) and passes a tap to whichever add button the form is showing. It never
  // submits the form itself, so the personalisation check can't be skipped.
  const productForm = $('[data-product-section] [data-product-form]');
  const stickyBar = productForm && $('[data-sticky-atc]');
  const actionsRow = productForm && $('.product__actions', productForm);
  if (stickyBar && actionsRow) {
    const stickyBtn = $('[data-sticky-add]', stickyBar);
    const stickyLabel = stickyBtn && $('[data-label]', stickyBtn);
    const progressEl = $('[data-sticky-progress]', stickyBar);
    const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    // One entry per required Globo question that's on show; null on products without any.
    const progress = () => {
      const groups = $$('.gpo-app .gpo-element', productForm).filter((g) => g.offsetParent !== null && $('.gpo-label.required', g));
      if (!groups.length) return null;
      const answered = (g) =>
        $$('input, select, textarea', g).some((f) =>
          f.type === 'checkbox' || f.type === 'radio' ? f.checked : f.type !== 'hidden' && f.value.trim() !== ''
        );
      return { done: groups.filter(answered).length, total: groups.length };
    };

    const sync = () => {
      const p = progress();
      if (progressEl) {
        const text = !p ? '' : p.done >= p.total
          ? stickyBar.dataset.progressDone
          : stickyBar.dataset.progress.replace('[done]', p.done).replace('[total]', p.total);
        progressEl.hidden = !p;
        if (progressEl.textContent !== text) progressEl.textContent = text;
      }
      if (stickyLabel && !stickyBtn.disabled) {
        stickyLabel.textContent = p && p.done < p.total ? stickyBar.dataset.labelPersonalise : theme.strings.addToCart;
      }
    };
    // Globo draws its fields after load and shows or hides some as others change.
    const syncSoon = debounce(sync, 80);
    productForm.addEventListener('input', syncSoon);
    productForm.addEventListener('change', syncSoon);
    new MutationObserver(syncSoon).observe(productForm, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    sync();

    // The bar's height feeds the Shopify Inbox chat lift (section-product.css). It grows
    // when the progress line appears, so it's measured whenever it changes.
    const setBarHeight = () => document.documentElement.style.setProperty('--sticky-atc-h', `${stickyBar.offsetHeight}px`);
    if ('ResizeObserver' in window) new ResizeObserver(setBarHeight).observe(stickyBar);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const on = !entry.isIntersecting;
        stickyBar.classList.toggle('is-visible', on);
        document.documentElement.classList.toggle('has-sticky-atc', on);
        setBarHeight();
      }).observe(actionsRow);
    }

    stickyBtn?.addEventListener('click', () => {
      const addButton = $$('.product__actions button[type="submit"]', productForm).find((b) => b.offsetParent !== null && !b.disabled);
      if (!addButton) {
        actionsRow.scrollIntoView({ behavior: smooth, block: 'center' });
        return;
      }
      addButton.click();
      // Globo writes its messages after its own click handler. Bring the first one into
      // view below the sticky header and put the cursor in that field.
      setTimeout(() => {
        const message = $$('.gpo-app .messages', productForm).find((m) => m.textContent.trim());
        const group = message && (message.closest('.gpo-element') || message);
        if (!group) return;
        group.scrollIntoView({ behavior: smooth, block: 'center' });
        const field = $('input:not([type="hidden"]), select, textarea', group);
        setTimeout(() => field?.focus({ preventScroll: true }), 450);
      }, 350);
    });
  }

  // Globo titles every tickbox "Checkbox". Name each one after what it asks the shopper to
  // confirm, as the live store does. The rules read the tickbox's own wording, never the
  // label, so running again changes nothing.
  if (productForm) {
    const confirmRules = [
      [/spelt correctly|spelling|no drafts/i, 'Confirm spelling'],
      [/natural wood|colour is subject|grain|subject to change/i, 'Confirm materials'],
      [/processing time|business day|urgent option/i, 'Confirm turnaround'],
      [/one name|adjusted my cart|per keychain|per keyring/i, 'Confirm your quantity'],
    ];
    const generic = /^(checkbox|confirm your quantity|confirm spelling|confirm materials|confirm turnaround|please confirm)$/i;
    const nameTickboxes = () =>
      $$('.gpo-app .gpo-label', productForm).forEach((label) => {
        const target = $('.label-content', label) || label;
        const current = target.textContent.trim();
        if (!generic.test(current)) return;
        const group = label.closest('.gpo-element') || label.parentElement;
        const wording = group ? group.textContent.replace(current, ' ') : '';
        const rule = confirmRules.find(([pattern]) => pattern.test(wording));
        const name = rule ? rule[1] : 'Please confirm';
        if (name !== current) target.textContent = name;
      });
    nameTickboxes();
    new MutationObserver(debounce(nameTickboxes, 80)).observe(productForm, { childList: true, subtree: true });
  }

  // Express checkout stays hidden (section-product.css) until we know Globo isn't running on
  // this product: it would skip the personalisation fields. If Globo turns up late, hide again.
  const dynamicCheckout = $('[data-product-section] [data-dynamic-checkout]');
  if (dynamicCheckout) {
    const globoOn = () => document.body.getAttribute('data-gpo-is-enabled') === 'true' || !!$('.gpo-app', productForm || document);
    const reveal = () => dynamicCheckout.classList.toggle('is-ready', !globoOn());
    const revealLater = () => setTimeout(reveal, 1500);
    if (document.readyState === 'complete') revealLater();
    else window.addEventListener('load', revealLater, { once: true });
    new MutationObserver(() => globoOn() && dynamicCheckout.classList.remove('is-ready')).observe(document.body, { attributes: true, attributeFilter: ['data-gpo-is-enabled'] });
  }

  // Load product recommendations into sections that ask for them.
  $$('[data-recommendations]').forEach(async (el) => {
    try {
      const html = await (await fetch(el.dataset.url)).text();
      const inner = $('[data-recommendations]', parseHTML(html));
      if (inner && inner.innerHTML.trim()) {
        el.innerHTML = inner.innerHTML;
        document.dispatchEvent(new CustomEvent('theme:content', { detail: el }));
      }
    } catch {
      /* optional */
    }
  });

  /* ------------------------------------------------------------------ */
  /* Accordions: animate <details> height                                */
  /* ------------------------------------------------------------------ */
  $$('details[data-accordion]').forEach((d) => {
    const summary = $('summary', d);
    const body = $('.accordion__body', d);
    if (!summary || !body || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (d.open) {
        body.animate([{ height: `${body.offsetHeight}px` }, { height: '0px' }], { duration: 320, easing: 'cubic-bezier(.22,1,.36,1)' }).onfinish = () => d.removeAttribute('open');
      } else {
        d.setAttribute('open', '');
        const h = body.offsetHeight;
        body.animate([{ height: '0px' }, { height: `${h}px` }], { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)' });
      }
    });
  });

  /* ------------------------------------------------------------------ */
  /* Copy buttons (gift card)                                            */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    try {
      await navigator.clipboard.writeText(btn.dataset.copy);
      const label = $('[data-label]', btn);
      if (label) label.textContent = btn.dataset.copied || 'Copied';
    } catch {
      /* clipboard unavailable */
    }
  });

  /* ------------------------------------------------------------------ */
  /* Hold animations that are off screen                                 */
  /* ------------------------------------------------------------------ */
  // A section well outside the screen gets .is-offscreen, which pauses its
  // looping glows, sways and twinkles (base.css). They pick up where they
  // left off before the section scrolls back into view, so nothing looks
  // different; the phone just stops drawing what nobody can see, which keeps
  // scrolling smooth.
  if ('IntersectionObserver' in window) {
    const offscreen = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.target.classList.toggle('is-offscreen', !e.isIntersecting)),
      { rootMargin: '300px 0px' }
    );
    document.querySelectorAll('.shopify-section, footer').forEach((s) => offscreen.observe(s));
  }

  /* ------------------------------------------------------------------ */
  /* Christmas links: a burst of gold flakes on the tap                  */
  /* ------------------------------------------------------------------ */
  // A tap on a Christmas link (header pill, tablet pill, first row of the phone
  // menu) throws gold snowflakes and sparks out from the finger, then follows
  // the link once they have had a moment to fly. Ctrl, Cmd, Shift and middle
  // clicks, and anyone who has asked for less motion, go straight through.
  const XMAS_LINKS = '.nav__link--christmas, .header__xmas, .menu-drawer__link--christmas';
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest(XMAS_LINKS);
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.target === '_blank' || !link.href) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !document.body.animate) return;
    const flake = $('.snowflake', link);
    if (!flake) return;
    e.preventDefault();

    const box = link.getBoundingClientRect();
    const fromKeys = e.detail === 0;
    const x = fromKeys ? box.left + box.width / 2 : e.clientX;
    const y = fromKeys ? box.top + box.height / 2 : e.clientY;
    const layer = document.createElement('div');
    layer.className = 'xmas-burst';
    layer.setAttribute('aria-hidden', 'true');
    layer.style.left = `${x}px`;
    layer.style.top = `${y}px`;

    const ring = document.createElement('span');
    ring.className = 'xmas-burst__ring';
    layer.appendChild(ring);
    ring.animate(
      [{ transform: 'translate(-50%, -50%) scale(0.3)', opacity: 0.9 }, { transform: 'translate(-50%, -50%) scale(2.6)', opacity: 0 }],
      { duration: 600, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' }
    );

    const count = 18;
    for (let i = 0; i < count; i++) {
      const bit = document.createElement('span');
      const isFlake = i % 3 !== 0;
      bit.className = isFlake ? 'xmas-burst__flake' : 'xmas-burst__spark';
      if (isFlake) bit.appendChild(flake.cloneNode(true));
      layer.appendChild(bit);
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.35;
      const dist = 48 + Math.random() * 52;
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist;
      const scale = isFlake ? 0.55 + Math.random() * 0.7 : 1;
      const spin = Math.round(Math.random() * 300 - 150);
      bit.animate(
        [
          { transform: 'translate(-50%, -50%) scale(0.2)', opacity: 1 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(${scale}) rotate(${spin}deg)`, opacity: 1, offset: 0.55 },
          { transform: `translate(calc(-50% + ${dx * 1.15}px), calc(-50% + ${dy * 1.15 + 22}px)) scale(${scale * 0.7}) rotate(${spin * 1.6}deg)`, opacity: 0 },
        ],
        { duration: 800 + Math.random() * 200, easing: 'cubic-bezier(.15,.75,.3,1)', fill: 'forwards' }
      );
    }
    document.body.appendChild(layer);
    link.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(0.93)' }, { transform: 'scale(1.05)' }, { transform: 'scale(1)' }],
      { duration: 420, easing: 'ease-out' }
    );
    setTimeout(() => layer.remove(), 1100);
    setTimeout(() => { window.location.href = link.href; }, 450);
  });
})();

/* ------------------------------------------------------------------ */
/* Shopify Inbox chat button: step aside for buttons and tick-boxes    */
/* ------------------------------------------------------------------ */
// The chat button sits in the bottom-right corner, where a button (the hero
// call to action, Add to cart, Checkout), a link, a form field or a tick-box
// can end up under it. While one does, html.chat-tuck fades the chat button
// out and lets taps through (see base.css), and it comes back once the way is
// clear. The app's shadow root is open, so the button's position can be read.
(() => {
  const PROTECT = 'a[href], button, [role="button"], summary, label, input:not([type="hidden"]), select, textarea';
  // Product photo links are big enough to tap elsewhere, so only things up to
  // this tall count (tick-box labels always count).
  const MAX_H = 80;
  const PAD = 8;
  const html = document.documentElement;
  let chat = null;
  let act = null;
  let raf = 0;

  const setTuck = (on) => {
    // only touch the class when it changes, so the observer below doesn't loop
    if (html.classList.contains('chat-tuck') !== on) html.classList.toggle('chat-tuck', on);
  };
  const shown = (el) => !el.checkVisibility
    || el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true, opacityProperty: true, visibilityProperty: true });
  const onScreen = (r) => r.width > 0 && r.height > 0
    && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
  const overlaps = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

  // The app swaps its inner markup after loading, so the button can sit one or
  // more shadow roots down (passed up with exportparts).
  const findActivator = (root, depth = 0) => {
    if (!root || depth > 4) return null;
    const hit = root.querySelector('[part~="activator"]');
    if (hit) return hit;
    for (const el of root.querySelectorAll('*')) {
      if (el.shadowRoot) {
        const found = findActivator(el.shadowRoot, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  const check = () => {
    raf = 0;
    if (!act || !act.isConnected) {
      chat = document.querySelector('shopify-chat');
      act = chat && findActivator(chat.shadowRoot);
      // never leave the button hidden when it can't be measured
      if (!act) { setTuck(false); return; }
    }
    if (chat.hasAttribute('open')) { setTuck(false); return; }
    const r = act.getBoundingClientRect();
    if (!r.width) return; // hidden, e.g. while a drawer is open
    // Measure from the centre with the computed size, as the app scales the
    // button in when the page loads.
    const cs = getComputedStyle(act);
    const w = parseFloat(cs.width) || r.width;
    const h = parseFloat(cs.height) || r.height;
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const box = { left: cx - w / 2 - PAD, right: cx + w / 2 + PAD, top: cy - h / 2 - PAD, bottom: cy + h / 2 + PAD };

    let hit = false;
    for (const el of document.querySelectorAll(PROTECT)) {
      // a styled tick-box usually hides its input and shows its label
      const tick = el.type === 'checkbox' || el.type === 'radio';
      const targets = tick ? [el, ...(el.labels || [])] : [el];
      for (const t of targets) {
        const tr = t.getBoundingClientRect();
        if (!tick && tr.height > MAX_H) continue;
        if (onScreen(tr) && overlaps(box, tr) && shown(t)) { hit = true; break; }
      }
      if (hit) break;
    }
    setTuck(hit);
  };
  const queue = () => { if (!raf) raf = requestAnimationFrame(check); };
  const later = () => { queue(); setTimeout(queue, 450); };

  addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', queue);
  addEventListener('load', later);
  document.addEventListener('cart:updated', later);
  // menus, accordions and option tick-boxes move things around after a tap
  document.addEventListener('click', later, true);
  // the chat button glides up over the sticky Add to cart bar on product pages
  new MutationObserver(later).observe(html, { attributes: true, attributeFilter: ['class'] });
  // the app adds its button some time after the page loads
  [0, 1000, 3000, 6000].forEach((t) => setTimeout(queue, t));
  if (window.customElements) customElements.whenDefined('shopify-chat').then(later);
})();
